import React from 'react';

import { colors } from '@/assets/styles/colors';
import { Card } from '@/components/ui/cards';
import { TextArea } from './TextArea';
import type { InputColor } from './Input.types';


/**
 * Alto mínimo del campo. Sale del diseño (bloque de 66px: campo + gap de 4px + contador) y no de
 * `rows`, que solo fija las líneas de texto y deja el borde pegado al placeholder.
 */
const FIELD_MIN_HEIGHT = '44px';

/**
 * Padding del Card. El vertical sale de las medidas del diseño: card de 143px de alto con el bloque
 * de campo (66px) centrado deja ~38px arriba y abajo. El horizontal se mantiene en 16px, que es lo
 * que el diseño deja a la izquierda del campo.
 */
const CARD_PADDING = '38px 16px';

export interface CommentBoxProps {
  value: string;
  onChange: (value: string) => void;
  /** El copy lo decide la pantalla: este componente es compartido y no resuelve textos. */
  placeholder: string;
  /** Obligatorio: el diseño no muestra label visible, así que es el único nombre accesible. */
  ariaLabel: string;
  maxLength: number;
  disabled?: boolean;
  required?: boolean;
  /** `'error'` para el estado de validación; el resto de variantes de InputColor también sirve. */
  color?: InputColor;
  /** Mensaje bajo el campo (por ejemplo el aviso de comentario obligatorio). */
  supportingText?: string;
  rows?: number;
  className?: string;
  /** Va al `<textarea>`; `TextArea` deriva de él el testid del contador (`-char-count`). */
  'data-testid'?: string;
  /** Testid del Card contenedor. Separado del anterior porque identifica el bloque, no el campo. */
  cardTestId?: string;
}

/**
 * Bloque de comentario del diseño: Card + campo de una línea con fondo gris y contador de
 * caracteres. Entrega el bloque completo, así que la pantalla NO debe envolverlo en su propio
 * `Card`.
 *
 * Es un wrapper delgado sobre `TextArea` — de ahí salen el contador, la resolución de colores por
 * estado, `disabled` y la accesibilidad. Solo ajusta lo que el diseño pide y `TextArea` no da por
 * defecto: fondo `neutralFA` en vez de blanco (sobre un Card blanco el campo se perdería), las
 * cuatro esquinas redondeadas y una sola línea de altura.
 */
export const CommentBox: React.FC<CommentBoxProps> = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
  maxLength,
  disabled = false,
  required = false,
  color = 'default',
  supportingText,
  rows = 1,
  className = '',
  'data-testid': dataTestId,
  cardTestId,
}) => (
  <Card
    /* Misma sombra que el resto de los bloques de contenido (IndicatorCard, tarjetas del
       dashboard): el default del Card es `elevation={2}`, pensado para contenedores de página,
       modales y dropdowns, y acá se veía más marcada que en los componentes vecinos. */
    elevation={1}
    padding={CARD_PADDING}
    className={`w-full ${className}`.trim()}
    data-testid={cardTestId}
  >
    <TextArea
      variant="standard"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      ariaLabel={ariaLabel}
      maxLength={maxLength}
      disabled={disabled}
      required={required}
      color={color}
      supportingText={supportingText}
      rows={rows}
      fullWidth
      showCharacterCount
      /* Se mergea sobre los colores base, así que el gris se mantiene también en error y en
         disabled — es el comportamiento buscado: el fondo es parte de la forma del campo, no
         del estado. */
      customColors={{ background: colors.grays.neutralFA }}
      /* Separación campo ↔ contador del diseño (gap 4px). El bloque del contador es hermano del
         campo dentro del contenedor de `TextArea`, así que el gap se define acá y no en el Card. */
      className="flex flex-col gap-1"
      inputStyle={{
        /* Neutraliza el `rounded-tl-none` de la variante para dejar las cuatro esquinas en 8px.
           Va por estilo inline y no por clase porque una clase competiría con la misma
           especificidad (ver `inputStyle` en TextArea). */
        borderTopLeftRadius: '8px',
        /* Alto del campo según el diseño: el bloque completo mide 66px = campo (44) + gap (4) +
           contador (~18). `rows` por sí solo deja el campo en ~42px, apretado contra el texto. */
        minHeight: FIELD_MIN_HEIGHT,
      }}
      data-testid={dataTestId}
    />
  </Card>
);

export default CommentBox;
